/**
 * Shared label for the "linked appointment" chip shown in the note form header and
 * the note detail panel, so both render identically.
 *
 * `startTime` arrives as a Pacific "YYYY-MM-DD HH:MM:SS" string (everything in the
 * legacy DB is Pacific), so it is reprojected into the viewer's timezone before
 * formatting — matching how NotesSection renders note dates.
 */
import { pacificToWallClock } from '@/lib/facilityTimezone';
import type { NoteAppointmentLink } from '@/v5/services/notes.service';

export function formatNoteApptLink(
  link: NoteAppointmentLink | undefined | null,
  userTz: string,
): string | null {
  if (!link) return null;

  const parts: string[] = [];
  if (link.appointmentTypeName) parts.push(link.appointmentTypeName);

  const when = formatPacificDateTime(link.startTime, userTz);
  if (when) parts.push(when);

  if (parts.length === 0) {
    return link.appointmentName || `Appointment #${link.appointmentId}`;
  }

  return parts.join(' · ');
}

function formatPacificDateTime(pacific: string | null, userTz: string): string | null {
  if (!pacific) return null;

  const [datePart, timePart = '00:00:00'] = pacific.split(' ');
  if (!datePart || datePart.startsWith('0000-00-00')) return null;

  const wall = pacificToWallClock(datePart, timePart.slice(0, 5), userTz);

  const [y, mo, d] = wall.date.split('-').map(Number);
  if (!y || !mo || !d) return null;

  // Noon anchor so the date label can't slip across a boundary via the local
  // timezone of the Date constructor.
  const dateLabel = new Date(y, mo - 1, d, 12).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const [hh, mi] = wall.time.split(':').map(Number);
  if (Number.isNaN(hh)) return dateLabel;

  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;

  return `${dateLabel}, ${h12}:${String(mi ?? 0).padStart(2, '0')} ${ampm}`;
}
